<template>
  <div class="auth-code">
    <button type="button" v-if="!signInStore.syncAppAuthCode || isCodeExpiredRef" class="btn btn-inline btn-link" @click="ensureCodeRequestedAsync">
        Get a new code
      </button>
      <div v-else class="code">
        {{ signInStore.syncAppAuthCode?.code }} <CopyTextButton :copy-text="signInStore.syncAppAuthCode?.code"/>
      </div>
      <div class="remaining" :class="{ show: showTimerRef}">
        Get a new code in {{ remainingTextRef }}
      </div>
  </div>
</template>

<style lang="scss" scoped>
.code {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 2rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: black;
}

.remaining {
  font-size: 0.875rem;
  color: transparent;

  &.show {
    color: #888;
  }
}
</style>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useTokenManager } from '@/auth/TokenManager'
import { useSignInStore } from './SignInStore'
import { DateTime, Duration } from 'luxon'
import CopyTextButton from '@/util/CopyTextButton.vue'

const props = defineProps<{
  alwaysShowTimer?: boolean
  autoRequest?: boolean
}>()

const emit = defineEmits([
  'authCodeUsed',
])

const tokenManager = useTokenManager()
const signInStore = useSignInStore()

const expiringDuration = 60 * 1000 // 1 minute

const remainingTimeRef = ref(0)
let timerHandle = 0

const isCodeExpiredRef = computed(() => signInStore.syncAppAuthCode && remainingTimeRef.value <= 0)
const showTimerRef = computed(() => signInStore.syncAppAuthCode && remainingTimeRef.value > 0 && (props.alwaysShowTimer || remainingTimeRef.value < expiringDuration))
const remainingTextRef = computed(() => {
  const d = Duration.fromMillis(remainingTimeRef.value)
  return d.as('minutes') >= 1 ? `${Math.ceil(d.as('minutes'))} min` : d.toFormat('m:ss')
})

watch(() => signInStore.syncAppAuthCodeUsed, () => {
  if (signInStore.syncAppAuthCodeUsed) {
    if (timerHandle) {
      clearInterval(timerHandle)
    }
    emit('authCodeUsed')
  }
})

onMounted(async () => {
  if (props.autoRequest) {
    await ensureCodeRequestedAsync()
  }
})

onUnmounted(() => {
  stopTimer()
})

async function ensureCodeRequestedAsync() {
  const token = await tokenManager.getAccessTokenAsync()
  if (token) {
    await signInStore.ensureAuthCodeRequestedAsync()
    updateRemainingTime()

    stopTimer() // just in case
    timerHandle = setInterval(() => {
      updateRemainingTime()
      if (remainingTimeRef.value == 0) {
        stopTimer()
      }
    }, 1000)
  }
}

function updateRemainingTime() {
  remainingTimeRef.value = Math.max(0, signInStore.syncAppAuthCode!.expirationDate.diff(DateTime.utc()).milliseconds)
}

function stopTimer() {
  if (timerHandle) {
    clearInterval(timerHandle)
    timerHandle = 0
  }
}

</script>
